/**
 * Celebration dialog shown after the client successfully signs a contract.
 *
 * Features a confetti-like particle animation, pulsing checkmark,
 * and animated text — all built with framer-motion (no extra deps).
 *
 * @module CelebrationDialog
 */

import React, { useMemo } from 'react';
import { Dialog, DialogContent, Typography, Button, Box } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { GREEN, DARK_BLUE } from '../theme';

interface CelebrationDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  message: string;
  subtitle: string;
  buttonText: string;
}

/** A single animated confetti particle. */
function ConfettiParticle({ delay, color, x }: { delay: number; color: string; x: number }) {
  return (
    <motion.div
      initial={{ y: -20, x, opacity: 1, rotate: 0, scale: 1 }}
      animate={{
        y: 400,
        x: x + (Math.random() - 0.5) * 120,
        opacity: 0,
        rotate: Math.random() * 720 - 360,
        scale: Math.random() * 0.5 + 0.3,
      }}
      transition={{ duration: 2.5 + Math.random(), delay, ease: 'easeOut' }}
      style={{
        position: 'absolute',
        top: 0,
        left: '50%',
        width: 8 + Math.random() * 8,
        height: 8 + Math.random() * 8,
        borderRadius: Math.random() > 0.5 ? '50%' : '2px',
        backgroundColor: color,
        pointerEvents: 'none',
      }}
    />
  );
}

/** Animated checkmark drawn with SVG path. */
function AnimatedCheckmark() {
  return (
    <motion.div
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ type: 'spring', stiffness: 200, damping: 12, delay: 0.2 }}
    >
      <Box
        sx={{
          width: 100,
          height: 100,
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${GREEN}, #66d44a)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mx: 'auto',
          mb: 3,
          boxShadow: `0 0 40px ${GREEN}66, 0 0 80px ${GREEN}33`,
        }}
      >
        <motion.svg
          width="50"
          height="50"
          viewBox="0 0 50 50"
          initial="hidden"
          animate="visible"
        >
          <motion.path
            d="M14 26 L22 34 L38 16"
            fill="none"
            stroke="white"
            strokeWidth="4"
            strokeLinecap="round"
            strokeLinejoin="round"
            variants={{
              hidden: { pathLength: 0 },
              visible: { pathLength: 1 },
            }}
            transition={{ duration: 0.5, delay: 0.5, ease: 'easeOut' }}
          />
        </motion.svg>
      </Box>
      {/* Pulsing ring */}
      <motion.div
        animate={{ scale: [1, 1.3, 1], opacity: [0.4, 0, 0.4] }}
        transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
        style={{
          position: 'absolute',
          top: 24,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 100,
          height: 100,
          borderRadius: '50%',
          border: `2px solid ${GREEN}`,
          pointerEvents: 'none',
        }}
      />
    </motion.div>
  );
}

export default function CelebrationDialog({
  open,
  onClose,
  title,
  message,
  subtitle,
  buttonText,
}: CelebrationDialogProps) {
  const confettiColors = [GREEN, '#66d44a', DARK_BLUE, '#ffd700', '#ff6b6b', '#4ecdc4', '#45b7d1', '#96e6a1'];

  const particles = useMemo(
    () =>
      Array.from({ length: 40 }, (_, i) => ({
        id: i,
        delay: Math.random() * 0.8,
        color: confettiColors[i % confettiColors.length],
        x: (Math.random() - 0.5) * 300,
      })),
    [],
  );

  return (
    <AnimatePresence>
      {open && (
        <Dialog
          open={open}
          onClose={onClose}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 4,
              overflow: 'hidden',
              background: `linear-gradient(180deg, ${DARK_BLUE} 0%, #003a50 100%)`,
              position: 'relative',
            },
          }}
        >
          {/* Confetti layer */}
          <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 1 }}>
            {particles.map((p) => (
              <ConfettiParticle key={p.id} delay={p.delay} color={p.color} x={p.x} />
            ))}
          </Box>

          <DialogContent
            sx={{
              textAlign: 'center',
              py: 6,
              px: 4,
              position: 'relative',
              zIndex: 2,
            }}
          >
            {/* Decorative glow */}
            <Box
              sx={{
                position: 'absolute',
                top: '20%',
                left: '50%',
                transform: 'translateX(-50%)',
                width: 200,
                height: 200,
                borderRadius: '50%',
                background: `radial-gradient(circle, ${GREEN}22 0%, transparent 70%)`,
                pointerEvents: 'none',
              }}
            />

            {/* Checkmark */}
            <Box sx={{ position: 'relative' }}>
              <AnimatedCheckmark />
            </Box>

            {/* Title */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.5 }}
            >
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  background: `linear-gradient(135deg, ${GREEN}, #96e6a1)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 1.5,
                }}
              >
                {title}
              </Typography>
            </motion.div>

            {/* Message */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.5 }}
            >
              <Typography variant="h6" sx={{ color: '#ffffffdd', mb: 1, fontWeight: 400 }}>
                {message}
              </Typography>
            </motion.div>

            {/* Subtitle */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1, duration: 0.5 }}
            >
              <Typography variant="body2" sx={{ color: '#ffffff88', mb: 4 }}>
                {subtitle}
              </Typography>
            </motion.div>

            {/* Button */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 1.3, type: 'spring', stiffness: 200, damping: 15 }}
            >
              <Button
                variant="contained"
                onClick={onClose}
                size="large"
                sx={{
                  px: 5,
                  py: 1.5,
                  borderRadius: 3,
                  fontWeight: 700,
                  fontSize: '1.05rem',
                  background: `linear-gradient(135deg, ${GREEN}, #66d44a)`,
                  boxShadow: `0 4px 20px ${GREEN}44`,
                  '&:hover': {
                    background: `linear-gradient(135deg, #66d44a, ${GREEN})`,
                    boxShadow: `0 6px 30px ${GREEN}66`,
                  },
                }}
              >
                {buttonText}
              </Button>
            </motion.div>
          </DialogContent>
        </Dialog>
      )}
    </AnimatePresence>
  );
}
